import React, { useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Button,
	useDisclosure,
} from "@chakra-ui/react"
import s from "./wearNowPanel.module.scss"
import router from "next/router"
import useRequest from "@/src/hooks/useRequest"
import {
	getTraitValue,
	parseDropAttributes,
	resolveDropFashionScore,
} from "@/utils/marketplace/helpers"

const AnyAlertDialog = AlertDialog as any
const AnyAlertDialogOverlay = AlertDialogOverlay as any
const AnyAlertDialogContent = AlertDialogContent as any
const AnyAlertDialogHeader = AlertDialogHeader as any
const AnyAlertDialogBody = AlertDialogBody as any
const AnyAlertDialogFooter = AlertDialogFooter as any
const AnyButton = Button as any
export type RevealedDrop = {
	dropId: string
	poolDrop?: any
}

interface WearNowPanelContentProps {
	wearNowModalControls?: any
	triggerOpen?: () => void
	triggerClose?: () => void
	isVisible?: boolean
	onRequestClose?: () => void
	openCartModal?: () => void
	openOrderModal?: () => void
	openInventoryModal?: () => void
	opendBoxes?: RevealedDrop[]
	revealedIndex?: number
	setRevealedIndex?: (index: number) => void
	setOpendBoxes?: (value: RevealedDrop[]) => void
	isMobile?: boolean
	devMode?: boolean
}

const WearNowPanelContent: React.FC<WearNowPanelContentProps> = ({
	wearNowModalControls,
	triggerOpen,
	triggerClose,
	isVisible = true,
	onRequestClose,
	openCartModal,
	openOrderModal,
	openInventoryModal,
	opendBoxes = [],
	revealedIndex = 0,
	setRevealedIndex,
	setOpendBoxes,
	isMobile = false,
	devMode = false,
}) => {
	const { getProduct } = useRequest()
	const [product, setProduct] = useState<any>(null)
	const [productName, setProductName] = useState<string>("")
	const [description, setDescription] = useState<string>("")
	const [attributes, setAttributes] = useState<any>(null)
	const [loader, setLoader] = useState(false)
	const [imageReady, setImageReady] = useState(false)
	const {
		isOpen: isComingSoonOpen,
		onOpen: onComingSoonOpen,
		onClose: onComingSoonClose,
	} = useDisclosure()
	const comingSoonCancelRef = useRef<HTMLButtonElement>(null)
	const activeRevealed = opendBoxes[revealedIndex] ?? null
	const hasMultipleReveals = opendBoxes.length > 1

	// Reset reveal state each time a new box is opened.
	useEffect(() => {
		setImageReady(false)
	}, [activeRevealed])

	useEffect(() => {
		document.addEventListener("keydown", onKeyDown)
	}, [])

	useEffect(() => {
		console.log("triggerOpen")
		triggerOpen()
	}, [])

	const applyDropToPanel = (temp_drop: any, poolDrop?: any) => {
		let name = temp_drop?.name || poolDrop?.name || ""
		if (temp_drop?.series_type) {
			name = `${temp_drop.series_type} - ${name}`
		}
		setProduct(temp_drop)
		setProductName(name)
		setDescription(temp_drop?.description || "")

		let temp_attr = {
			image: poolDrop?.image || temp_drop?.image,
			name: poolDrop?.name || temp_drop?.name,
			rarity: poolDrop?.rarity_type || temp_drop?.rarity_type,
			fashionScore:
				resolveDropFashionScore(temp_drop) ??
				resolveDropFashionScore(poolDrop),
			health: 1,
			speed: 1,
			power: 1,
			hapeHolders: { dailyCap: "+2%", bananaIncome: "+1%" },
			regularUsers: { dailyCap: "1%", bananaIncome: "0.2%" },
		}
		const attrSource = temp_drop?.attributes || poolDrop?.attributes
		temp_attr.health = getTraitValue(attrSource, "Health") ?? 1
		temp_attr.speed = getTraitValue(attrSource, "Speed") ?? 1
		temp_attr.power = getTraitValue(attrSource, "Power") ?? 1

		let hapeHolders = { bananaIncome: "", dailyCap: "" }
		let regularUsers = { bananaIncome: "", dailyCap: "" }
		parseDropAttributes(attrSource).map((a) => {
			if (a.is_regular_users) {
				if (a.trait_type == "BananaIncome") {
					regularUsers.bananaIncome = a.value
				}
				if (a.trait_type == "DailyBananaCap") {
					regularUsers.dailyCap = a.value
				}
			} else {
				if (a.trait_type == "BananaIncome") {
					hapeHolders.bananaIncome = a.value
				}
				if (a.trait_type == "DailyBananaCap") {
					hapeHolders.dailyCap = a.value
				}
			}
		})
		temp_attr.hapeHolders = hapeHolders
		temp_attr.regularUsers = regularUsers
		setAttributes(temp_attr)
	}

	const loadRevealedProduct = async (revealed: RevealedDrop) => {
		setLoader(true)
		try {
			// Dev preview uses fixture poolDrop only — never fetch or mutate inventory.
			if (
				(devMode || String(revealed.dropId).startsWith("dev-")) &&
				revealed.poolDrop
			) {
				applyDropToPanel(revealed.poolDrop, revealed.poolDrop)
				setImageReady(true)
				return
			}

			const resProduct = await getProduct(revealed.dropId)
			console.log("resProduct", resProduct)
			if (resProduct?.code !== 200) {
				console.log(resProduct?.message)
				return
			}

			const temp_drop = resProduct.data.current_drop
			if (!temp_drop) {
				console.log(resProduct.message)
				return
			}

			applyDropToPanel(temp_drop, revealed.poolDrop)
		} finally {
			setLoader(false)
		}
	}

	useEffect(() => {
		if (activeRevealed?.dropId) {
			console.log("activeRevealed", activeRevealed)
			loadRevealedProduct(activeRevealed)
		}
	}, [activeRevealed])

	const handleClose = () => {
		console.log("handleClose---->")
		if (devMode) {
			if (setRevealedIndex) {
				setRevealedIndex(0)
			}
			return
		}
		if (setOpendBoxes) {
			setOpendBoxes([])
		}
		if (setRevealedIndex) {
			setRevealedIndex(0)
		}
	}

	const showPreviousReveal = () => {
		if (!setRevealedIndex || revealedIndex <= 0) return
		setRevealedIndex(revealedIndex - 1)
	}

	const showNextReveal = () => {
		if (!setRevealedIndex || revealedIndex >= opendBoxes.length - 1) return
		setRevealedIndex(revealedIndex + 1)
	}

	useEffect(() => {
		if (!hasMultipleReveals) return
		const onArrow = (event: KeyboardEvent) => {
			if (event.key === "ArrowLeft") {
				event.preventDefault()
				showPreviousReveal()
			}
			if (event.key === "ArrowRight") {
				event.preventDefault()
				showNextReveal()
			}
		}
		document.addEventListener("keydown", onArrow)
		return () => document.removeEventListener("keydown", onArrow)
	}, [hasMultipleReveals, revealedIndex, opendBoxes.length])

	const revealNavChevron = (direction: "prev" | "next") => (
		<svg
			width="12"
			height="12"
			viewBox="0 0 12 12"
			fill="none"
			aria-hidden="true"
		>
			<path
				d={
					direction === "prev"
						? "M7.5 2.25 3.75 6 7.5 9.75"
						: "M4.5 2.25 8.25 6 4.5 9.75"
				}
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)

	const revealNavigation = hasMultipleReveals ? (
		<div className={s.revealNav} aria-label="Opened items">
			<button
				type="button"
				className={s.revealNavArrow}
				onClick={showPreviousReveal}
				disabled={revealedIndex <= 0}
				aria-label="Previous item"
			>
				{revealNavChevron("prev")}
			</button>
			<div className={s.revealDots} aria-label="Item position">
				{opendBoxes.map((_, index) => (
					<button
						type="button"
						key={index}
						className={`${s.revealDot}${
							index === revealedIndex ? ` ${s.revealDotActive}` : ""
						}`}
						aria-label={`Item ${index + 1} of ${opendBoxes.length}`}
						aria-current={index === revealedIndex ? "true" : undefined}
						onClick={() => setRevealedIndex?.(index)}
					>
						<span className={s.revealDotMark} />
					</button>
				))}
			</div>
			<button
				type="button"
				className={s.revealNavArrow}
				onClick={showNextReveal}
				disabled={revealedIndex >= opendBoxes.length - 1}
				aria-label="Next item"
			>
				{revealNavChevron("next")}
			</button>
		</div>
	) : null

	const onKeyDown = (event) => {
		if (event.keyCode === 27) {
			handleClose()
			triggerClose()
		}
	}

	const handleWearNowClick = () => {
		onComingSoonOpen()
	}

	const continueToMyHapes = () => {
		onComingSoonClose()
		if (devMode) return
		router.push("/my-hapes")
		handleClose()
		triggerClose()
	}

	const handleRequestClose = () => {
		console.log("handleRequestClose---->")
		handleClose()
		if (onRequestClose) {
			onRequestClose()
		}
		console.log("triggerClose---->")
		triggerClose()
	}

	const productImage = product?.image || attributes?.image || ""
	const wearNowPadsBg = "/static/figma/wear-now-top-bottom.png"

	return (
		<div>
			<motion.div className={s.pane} animate={wearNowModalControls}>
				<div className={"frame"}>
					{isMobile ? (
						<div className="mobile-layout">
							<button
								className="abs mobile-close"
								aria-label="Close"
								onClick={handleRequestClose}
							>
								×
							</button>
							{loader ? (
								<>
									<div className="skeleton skeleton-title" />
									<div className="skeleton skeleton-desc" />
								</>
							) : (
								<>
									<h2 className="mobile-product-title">{productName}</h2>
									<p className="mobile-product-desc">A new addition to your wardrobe</p>
									{/* <p className="mobile-product-desc">{description}</p> */}
									
								</>
							)}

							<div
								className="mobile-featured-stage"
								aria-label={productName || "Unboxed item"}
							>
								<img
									className={`featured-pads pads-top${imageReady ? " pads--open" : ""}`}
									src={wearNowPadsBg}
									alt=""
									aria-hidden="true"
								/>
								<img
									className={`featured-pads pads-bottom${imageReady ? " pads--open" : ""}`}
									src={wearNowPadsBg}
									alt=""
									aria-hidden="true"
								/>
								{loader ? (
									<span className="wn-loader" />
								) : (
									<img
										key={activeRevealed?.dropId || "featured-mobile"}
										className={`featured-item${imageReady ? " featured-item--revealed" : ""}`}
										src={productImage}
										alt={productName || "Unboxed item"}
										onLoad={() => setImageReady(true)}
									/>
								)}
							</div>

							{revealNavigation}

							<div className="mobile-cta-row">
								<button
									className="btn primary mobile-wear-btn"
									onClick={handleWearNowClick}
								>
									Wear Now
								</button>
							</div>

							<div className="mobile-side">
								<div className="attributes">
									<span className="attr-title">Fashion Index Bonus</span>
									<div className="attr-row">
										<span className="attr-value">
											{attributes?.fashionScore ?? "—"}
										</span>
										<img
											className="attr-icon"
											src="/static/figma/ware-now/fashion_score_icon.svg"
											alt="fashion-score-icon"
										/>
										<span className="attr-label">Fashion Score</span>
									</div>
								</div>

								<div className="user-benefits">
									<div className="benefit-section">
										<h3 className="benefit-title">Hape holders</h3>
										<div className="benefit-row">
											<span className="benefit-value">
												+{attributes?.hapeHolders?.dailyCap || "2%"}
											</span>
											<span className="benefit-label">Daily Banana Cap</span>
										</div>
										<div className="benefit-row">
											<span className="benefit-value">
												+{attributes?.hapeHolders?.bananaIncome || "1%"}
											</span>
											<span className="benefit-label">Banana Income</span>
										</div>
									</div>
									<div className="benefit-section">
										<h3 className="benefit-title">Regular users</h3>
										<div className="benefit-row">
											<span className="benefit-value">
												{attributes?.regularUsers?.dailyCap || "1%"}
											</span>
											<span className="benefit-label">Daily Banana Cap</span>
										</div>
										<div className="benefit-row">
											<span className="benefit-value">
												{attributes?.regularUsers?.bananaIncome || "0.2%"}
											</span>
											<span className="benefit-label">Banana Income</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : (
						<>
							<button
								className="abs close"
								aria-label="Close"
								onClick={handleRequestClose}
							>
								<img src="/static/img/close.svg" alt="Close" />
							</button>
							<div className="reveal-canvas">
								<div className="reveal-body">
									<div className="reveal-copy">
										{loader ? (
											<>
												<div className="skeleton skeleton-title" />
												<div className="skeleton skeleton-title" />
												<div className="skeleton skeleton-desc" />
											</>
										) : (
											<>
												<h2 className="product-title">{productName}</h2>
												<p className="product-desc">A new addition to your wardrobe</p>
												{/* <p className="product-desc">{description}</p> */}
											</>
										)}
										<div className="attributes">
											<span className="attr-title">Fashion Index Bonus</span>
											<div className="line-break"></div>
											<div className="attr-row">
												<span className="attr-value">
													{attributes?.fashionScore ?? "—"}
												</span>
												<img
													className="attr-icon"
													src="/static/figma/ware-now/fashion_score_icon.svg"
													alt="fashion-score-icon"
												/>
												<span className="attr-label">Fashion Score</span>
											</div>
										</div>
									</div>
									<div className="reveal-stack">
										<div className="reveal-hero">
											<div
												className="featured-stage"
												aria-label={productName || "Unboxed item"}
											>
												<img
													className={`featured-pads pads-top${imageReady ? " pads--open" : ""}`}
													src={wearNowPadsBg}
													alt=""
													aria-hidden="true"
												/>
												<img
													className={`featured-pads pads-bottom${imageReady ? " pads--open" : ""}`}
													src={wearNowPadsBg}
													alt=""
													aria-hidden="true"
												/>
												{loader ? (
													<span className="wn-loader" />
												) : (
													<img
														key={activeRevealed?.dropId || "featured-desktop"}
														className={`featured-item${imageReady ? " featured-item--revealed" : ""}`}
														src={productImage}
														alt={productName || "Unboxed item"}
														onLoad={() => setImageReady(true)}
													/>
												)}
											</div>
										</div>
										{revealNavigation}
										<div className="cta-row">
											<button
												className="btn primary"
												onClick={handleWearNowClick}
											>
												Wear Now
											</button>
										</div>
									</div>
								</div>
							</div>
						</>
					)}

					<style jsx>{`
						.frame {
							position: relative;
							z-index: 1;
							width: 100%;
							min-height: 100vh;
							color: #fff;
							font-family: "Konnect", sans-serif;
							margin: 0 auto;
							transition:
								transform 0.42s ease,
								opacity 0.42s ease;
							box-sizing: border-box;
							overflow: hidden;
						}
						.frame::after {
							content: "";
							position: absolute;
							left: 0;
							right: 0;
							bottom: 0;
							height: min(55vh, 520px);
							pointer-events: none;
							background: linear-gradient(
								to top,
								rgba(0, 0, 0, 0.72) 0%,
								rgba(0, 0, 0, 0.35) 45%,
								transparent 100%
							);
							z-index: 0;
						}
						.slide-hidden {
							transform: translateX(-100%);
							opacity: 0;
							pointer-events: none;
						}
						.slide-show {
							transform: translateX(0);
							opacity: 1;
							pointer-events: auto;
						}
						.abs {
							position: absolute;
						}

						/* 返回箭头与标题左对齐：使用相对单位 */
						.back {
							left: clamp(48px, 6vw, 100px);
							top: clamp(48px, 6vh, 80px);
							background: transparent;
							border: none;
							color: #fff;
							font-size: clamp(20px, 2vw, 28px);
							cursor: pointer;
							z-index: 10;
						}
						.title {
							left: clamp(10px, 1vw, 20px);
							top: clamp(48px, 6vh, 96px);
							margin: 0;
							font-weight: 500;
							font-size: clamp(24px, 3vw, 36px);
							line-height: 1.2;
							text-align: left;
						}
						.close {
							right: clamp(48px, 6vw, 100px);
							top: clamp(48px, 6vh, 80px);
							color: #fff;
							width: clamp(28px, 3vw, 40px);
							height: clamp(28px, 3vw, 40px);
							display: inline-flex;
							align-items: center;
							justify-content: center;
							cursor: pointer;
							z-index: 10;
						}

						/* Reveal canvas: copy left, stage+button centered as one stack */
						.reveal-canvas {
							position: relative;
							z-index: 1;
							display: flex;
							flex-direction: column;
							min-height: 100vh;
							width: 100%;
							max-width: none;
							margin: 0;
							padding: clamp(48px, 6vh, 80px) clamp(48px, 6vw, 100px)
								clamp(120px, 14vh, 180px);
							box-sizing: border-box;
						}

						.reveal-body {
							display: grid;
							grid-template-columns: minmax(220px, 340px) 1fr;
							grid-template-rows: 1fr auto;
							flex: 1;
							min-height: 0;
							align-items: end;
						}

						.reveal-copy {
							grid-column: 1;
							grid-row: 2;
							align-self: start;
							z-index: 2;
							padding-right: clamp(16px, 2vw, 32px);
							padding-top: 180px;
						}

						.reveal-stack {
							grid-column: 1 / -1;
							grid-row: 2;
							display: flex;
							flex-direction: column;
							align-items: center;
							gap: clamp(12px, 1.5vh, 20px);
							width: 100%;
						}

						.reveal-hero {
							display: flex;
							justify-content: center;
							width: 100%;
							max-width: min(680px, 72%);
						}

						.featured-stage,
						.mobile-featured-stage {
							position: relative;
							width: 100%;
							aspect-ratio: 809 / 805;
						}

						.featured-pads {
							display: block;
							position: absolute;
							inset: 0;
							width: 100%;
							height: 100%;
							object-fit: contain;
							pointer-events: none;
							user-select: none;
							transition: transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);
						}

						/* Top half: starts pushed down (closed), eases to natural position on reveal */
						.pads-top {
							clip-path: inset(0 0 50% 0);
							transform-origin: center bottom;
							transform: translateY(14%);
						}
						.pads-top.pads--open {
							transform: translateY(0);
						}

						/* Bottom half: starts pushed up (closed), eases to natural position on reveal */
						.pads-bottom {
							clip-path: inset(50% 0 0 0);
							transform-origin: center top;
							transform: translateY(-14%);
						}
						.pads-bottom.pads--open {
							transform: translateY(0);
						}

						.featured-item {
							position: absolute;
							left: 49%;
							top: 45%;
							transform: translate(-50%, -50%);
							width: 50%;
							object-fit: contain;
							z-index: 1;
							opacity: 0;
						}

						.featured-item--revealed {
							animation: wn-reveal 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
						}

						@keyframes wn-reveal {
							from {
								opacity: 0;
								transform: translate(-50%, -44%) scale(0.88);
								filter: blur(6px);
							}
							to {
								opacity: 1;
								transform: translate(-50%, -50%) scale(1);
								filter: blur(0);
							}
						}

						/* Dot-burst loader shown while product loads */
						.wn-loader {
							--color-1: #fff;
							--size: 0.25px;
							position: absolute;
							left: 50%;
							top: 45%;
							transform: translate(-50%, -50%);
							width: calc(48 * var(--size));
							height: calc(48 * var(--size));
							z-index: 2;
						}
						.wn-loader::before,
						.wn-loader::after {
							content: "";
							position: absolute;
							left: 50%;
							top: 50%;
							transform: translate(-50%, -50%);
							width: 48em;
							height: 48em;
							background-image: radial-gradient(
									circle calc(10 * var(--size)),
									var(--color-1) 100%,
									transparent 0
								),
								radial-gradient(
									circle calc(10 * var(--size)),
									var(--color-1) 100%,
									transparent 0
								),
								radial-gradient(
									circle calc(10 * var(--size)),
									var(--color-1) 100%,
									transparent 0
								),
								radial-gradient(
									circle calc(10 * var(--size)),
									var(--color-1) 100%,
									transparent 0
								),
								radial-gradient(
									circle calc(10 * var(--size)),
									var(--color-1) 100%,
									transparent 0
								),
								radial-gradient(
									circle calc(10 * var(--size)),
									var(--color-1) 100%,
									transparent 0
								),
								radial-gradient(
									circle calc(10 * var(--size)),
									var(--color-1) 100%,
									transparent 0
								),
								radial-gradient(
									circle calc(10 * var(--size)),
									var(--color-1) 100%,
									transparent 0
								);
							background-position:
								0em -18em,
								0em 18em,
								18em 0em,
								-18em 0em,
								13em -13em,
								-13em -13em,
								13em 13em,
								-13em 13em;
							background-repeat: no-repeat;
							font-size: calc(0.5 * var(--size));
							border-radius: 50%;
							animation: wn-blast 1s ease-in infinite;
						}
						.wn-loader::after {
							font-size: calc(1 * var(--size));
							background: var(--color-1);
							animation: wn-bounce 1s ease-in infinite;
						}
						@keyframes wn-bounce {
							0%,
							100% {
								font-size: calc(0.75 * var(--size));
							}
							50% {
								font-size: calc(1.5 * var(--size));
							}
						}
						@keyframes wn-blast {
							0%,
							40% {
								font-size: calc(0.5 * var(--size));
							}
							70% {
								opacity: 1;
								font-size: calc(4 * var(--size));
							}
							100% {
								font-size: calc(6 * var(--size));
								opacity: 0;
							}
						}

						.skeleton {
							background: linear-gradient(
								90deg,
								rgba(255, 255, 255, 0.07) 0%,
								rgba(255, 255, 255, 0.18) 50%,
								rgba(255, 255, 255, 0.07) 100%
							);
							background-size: 200% 100%;
							border-radius: 6px;
							animation: wn-shimmer 1.4s ease-in-out infinite;
						}
						.skeleton-title {
							height: 22px;
							width: 68%;
							margin-bottom: 10px;
						}
						.skeleton-desc {
							height: 14px;
							width: 90%;
							margin-bottom: 6px;
						}
						@keyframes wn-shimmer {
							0% {
								background-position: 200% 0;
							}
							100% {
								background-position: -200% 0;
							}
						}

						.pulled-label {
							margin: 0 0 6px 0;
							width: 100%;
							max-width: 320px;
							color: #fff;
							font-family: Konnect, sans-serif;
							font-size: clamp(12px, 1.1vw, 14px);
							font-weight: 700;
							line-height: 1.2;
							letter-spacing: 0.04em;
							text-align: left;
						}

						.product-title {
							margin: 0 0 clamp(6px, 1vh, 12px) 0;
							width: 100%;
							max-width: 320px;
							color: #fff;
							font-family: Konnect, sans-serif;
							font-size: clamp(16px, 1.5vw, 23px);
							font-weight: 500;
							line-height: 1.25;
							text-align: left;
						}

						.product-desc {
							margin: 0 0 clamp(16px, 2.5vh, 28px) 0;
							width: 100%;
							max-width: 320px;
							color: #919895;
							font-family: Konnect, sans-serif;
							font-size: clamp(12px, 1.05vw, 14px);
							font-weight: 400;
							line-height: 1.45;
							text-align: left;
						}

						/* 属性列表 */
						.attributes {
							width: 100%;
							max-width: 320px;
						}
						.attr-row {
							display: flex;
							align-items: center;
							margin-bottom: clamp(6px, 1vh, 10px);
							flex-wrap: nowrap;
							gap: 6px;
						}
						.attr-title {
							font-size: clamp(10px, 0.95vw, 13px);
							color: #fff;
							margin-bottom: clamp(4px, 0.5vh, 8px);
						}
						.attr-label {
							font-size: clamp(10px, 0.95vw, 13px);
							color: #919895;
							line-height: 1;
							margin: 0;
						}
						.attr-value {
							font-size: clamp(10px, 0.95vw, 13px);
							color: #fff;
							font-weight: 600;
							line-height: 1;
							flex-shrink: 0;
						}
						.line-break {
							height: 1px;
							width: 100%;
							background: #555555;
							margin-bottom: clamp(6px, 1vh, 10px);
						}
						.attr-icon {
							width: clamp(11px, 1.2vw, 15px);
							height: clamp(10px, 1.1vw, 14px);
							flex-shrink: 0;
							display: block;
						}

						/* 用户收益区域 */
						.user-benefits {
							width: 100%;
							margin-top: clamp(16px, 3vh, 28px);
						}
						.benefit-section {
							margin-bottom: clamp(16px, 3vh, 28px);
						}
						.benefit-title {
							margin: 0 0 clamp(8px, 1.5vh, 14px) 0;
							font-size: clamp(11px, 1.2vw, 15px);
							font-weight: 400;
							color: #fff;
						}
						.benefit-row {
							display: flex;
							align-items: center;
							margin-bottom: clamp(6px, 1vh, 10px);
							flex-wrap: wrap;
							gap: clamp(8px, 1vw, 12px);
						}
						.benefit-label {
							font-size: clamp(11px, 1.2vw, 15px);
							color: #919895;
							margin-left: clamp(50px, 9vw, 93px);
						}
						.benefit-value {
							font-size: clamp(11px, 1.2vw, 15px);
							color: #fff;
							font-weight: 600;
							min-width: 30px;
						}

						/* Button sits in centered stack, directly under stage */
						.cta-row {
							display: flex;
							justify-content: center;
							flex-shrink: 0;
							padding: 0;
							width: 100%;
						}
						.btn {
							min-width: clamp(150px, 20vw, 220px);
							height: clamp(40px, 5vh, 52px);
							padding: 0 clamp(14px, 2vw, 24px);
							border-radius: 24px;
							border: 1px solid #ffffff;
							font-size: clamp(12px, 1.5vw, 16px);
							font-weight: 400;
							display: inline-flex;
							align-items: center;
							justify-content: center;
							cursor: pointer;
							white-space: nowrap;
						}
						.primary {
							background: #3ddc84;
							color: #fff;
							border-color: #3ddc84;
						}
						.primary:hover {
							filter: brightness(0.95);
						}

						/* 响应式断点优化 */
						@media (max-width: 1400px) {
							.reveal-canvas {
								padding-left: clamp(40px, 5vw, 80px);
								padding-right: clamp(40px, 5vw, 80px);
							}

							.featured-stage,
							.reveal-hero {
								max-width: min(620px, 76%);
							}
						}

						@media (max-width: 1024px) {
							.reveal-canvas {
								padding: clamp(80px, 12vh, 140px) clamp(32px, 5vw, 64px)
									clamp(96px, 12vh, 140px);
							}

							.back {
								left: clamp(32px, 5vw, 64px);
								top: clamp(80px, 12vh, 140px);
							}

							.close {
								right: clamp(32px, 5vw, 64px);
								top: clamp(40px, 5vh, 64px);
							}

							.reveal-body {
								grid-template-columns: 1fr;
								grid-template-rows: auto auto;
								gap: clamp(24px, 4vh, 40px);
							}

							.reveal-copy {
								grid-column: 1;
								grid-row: 1;
								padding-right: 0;
								padding-top: 0;
							}

							.reveal-stack {
								grid-column: 1;
								grid-row: 2;
							}

							.pulled-label,
							.product-title,
							.product-desc,
							.attributes {
								max-width: 100%;
								margin-left: auto;
								margin-right: auto;
							}

							.pulled-label,
							.product-title {
								text-align: left;
							}

							.product-desc,
							.attributes {
								text-align: center;
							}

							.reveal-hero,
							.featured-stage {
								max-width: min(560px, 88vw);
							}

							.featured-item {
								width: 44%;
								max-height: 30%;
							}

							.attributes,
							.user-benefits {
								width: 100%;
							}
						}

						@media (max-width: 768px) {
							.back {
								left: clamp(10px, 2vw, 16px);
								top: max(clamp(12px, 2vh, 24px), env(safe-area-inset-top));
								font-size: clamp(18px, 4vw, 24px);
							}
							.close {
								right: clamp(10px, 2vw, 16px);
								top: max(clamp(10px, 1.5vh, 20px), env(safe-area-inset-top));
								width: clamp(32px, 8vw, 40px);
								height: clamp(32px, 8vw, 40px);
							}
							.reveal-canvas {
								padding-top: clamp(70px, 10vh, 120px);
								padding-left: clamp(28px, 4vw, 48px);
								padding-right: clamp(28px, 4vw, 48px);
								row-gap: clamp(20px, 4vh, 40px);
							}

							.reveal-hero,
							.featured-stage {
								max-width: min(500px, 88vw);
							}

							.product-title {
								font-size: clamp(15px, 3.5vw, 20px);
							}

							.product-desc {
								font-size: clamp(12px, 2.5vw, 15px);
								line-height: 1.5;
							}

							.attr-title,
							.attr-label,
							.attr-value {
								font-size: clamp(11px, 2.2vw, 14px);
							}
							.attr-icon {
								width: clamp(12px, 2.5vw, 16px);
								height: clamp(11px, 2.2vw, 14px);
							}

							.benefit-title,
							.benefit-label,
							.benefit-value {
								font-size: clamp(11px, 2.2vw, 14px);
							}
							.benefit-label {
								margin-left: clamp(28px, 6vw, 64px);
							}

							.cta-row {
								padding: 0 clamp(12px, 3vw, 20px);
							}
							.btn {
								min-width: clamp(140px, 40vw, 200px);
								height: clamp(44px, 6vh, 52px);
								font-size: clamp(13px, 2vw, 15px);
							}
						}

						@media (max-width: 480px) {
							.reveal-canvas {
								padding-top: clamp(56px, 8vh, 100px);
								padding-bottom: clamp(24px, 4vh, 48px);
								row-gap: clamp(16px, 3vh, 32px);
							}

							.featured-stage {
								max-width: min(360px, 90vw);
							}

							.product-title {
								font-size: clamp(16px, 4.5vw, 22px);
								min-height: 40px;
							}

							.product-desc {
								font-size: 12px;
							}

							.benefit-label {
								margin-left: 24px;
							}

							.user-benefits {
								margin-top: clamp(12px, 2vh, 20px);
							}
							.benefit-section {
								margin-bottom: clamp(12px, 2vh, 20px);
							}

							.cta-row {
								margin-top: clamp(24px, 4vh, 48px);
							}
							.btn {
								width: 100%;
								max-width: 320px;
								min-width: unset;
								height: 48px;
								font-size: 14px;
							}
						}

						/* 超小屏：安全区 + 更紧凑布局 */
						@media (max-width: 375px) {
							.back {
								left: 8px;
								top: max(10px, env(safe-area-inset-top));
								font-size: 20px;
							}
							.close {
								right: 8px;
								top: max(8px, env(safe-area-inset-top));
								width: 36px;
								height: 36px;
							}
							.reveal-canvas {
								padding-top: max(
									clamp(48px, 7vh, 80px),
									calc(env(safe-area-inset-top) + 8px)
								);
								padding-left: 10px;
								padding-right: 10px;
								padding-bottom: max(
									clamp(20px, 3vh, 40px),
									env(safe-area-inset-bottom)
								);
								row-gap: clamp(12px, 2.5vh, 24px);
							}

							.featured-stage {
								max-width: 94vw;
							}

							.product-title {
								font-size: 15px;
								min-height: 36px;
							}

							.product-desc {
								font-size: 11px;
							}

							.attr-row {
								margin-bottom: 6px;
								gap: 6px;
							}
							.attr-title {
								font-size: 11px;
							}
							.attr-label {
								font-size: 11px;
							}
							.attr-value {
								font-size: 11px;
							}
							.line-break {
								margin-bottom: 6px;
							}

							.benefit-title {
								font-size: 11px;
							}
							.benefit-label {
								margin-left: 16px;
								font-size: 11px;
							}
							.benefit-value {
								font-size: 11px;
							}
							.benefit-row {
								margin-bottom: 6px;
							}

							.cta-row {
								margin-top: clamp(20px, 3vh, 40px);
								padding: 0 10px;
							}
							.btn {
								width: 100%;
								max-width: 100%;
								height: 48px;
								font-size: 13px;
							}
						}

						@media (max-height: 800px) {
							.reveal-canvas {
								padding-top: clamp(60px, 8vh, 120px);
								row-gap: clamp(15px, 3vh, 30px);
								padding-bottom: clamp(80px, 10vh, 120px);
							}

							.reveal-copy {
								padding-top: clamp(100px, 14vh, 160px);
							}

							.featured-stage {
								max-width: min(500px, 70vw);
							}

							.cta-row {
								padding: 0;
							}
						}

						@media (max-height: 600px) {
							.reveal-canvas {
								padding-top: clamp(48px, 6vh, 80px);
								row-gap: clamp(10px, 2vh, 20px);
								padding-bottom: clamp(64px, 8vh, 96px);
							}

							.reveal-copy {
								padding-top: clamp(64px, 10vh, 100px);
							}

							.product-title {
								font-size: clamp(14px, 2vw, 18px);
							}

							.featured-stage {
								max-width: min(400px, 65vw);
							}

							.cta-row {
								padding: 0;
							}
							.btn {
								height: 44px;
								font-size: 13px;
							}
						}

						@media (min-width: 1920px) {
							.reveal-canvas {
								padding-left: 100px;
								padding-right: 100px;
							}
						}

						.mobile-layout {
							position: relative;
							width: 100%;
							min-height: 100vh;
							padding: 98px 48px 56px;
							box-sizing: border-box;
							color: #fff;
							background: linear-gradient(180deg, #0c1020 0%, #000 100%);
						}
						.mobile-close {
							right: 23px;
							top: 83px;
							width: 31px;
							height: 31px;
							border-radius: 999px;
							border: none;
							background: #fff;
							color: #111;
							font-size: 28px;
							line-height: 1;
							display: inline-flex;
							align-items: center;
							justify-content: center;
							padding: 0;
							cursor: pointer;
						}
						.mobile-layout .pulled-label {
							max-width: 326px;
							margin-bottom: 8px;
							font-size: 13px;
							text-align: left;
						}
						.mobile-product-title {
							margin: 0;
							font-size: 23.613px;
							line-height: 26.762px;
							font-weight: 500;
							color: #fff;
							text-align: left;
						}
						.mobile-product-desc {
							margin: 14px 0 0;
							font-size: 13px;
							line-height: 1.5;
							color: #f2f2f2;
							max-width: 326px;
						}
						.mobile-featured-stage {
							width: 100%;
							max-width: 420px;
							margin: 28px auto 0;
						}

						.mobile-featured-stage .featured-item {
							width: 40%;
							max-height: 26%;
						}
						.mobile-cta-row {
							display: flex;
							justify-content: center;
							margin-top: 2px;
						}
						.mobile-wear-btn {
							width: 146px;
							min-width: 146px;
							height: 43px;
							border-radius: 25px;
							font-size: 13px;
							font-weight: 400;
							padding: 0;
						}
						.mobile-side {
							width: 100%;
							max-width: 328px;
							margin: 40px auto 0;
						}
						.mobile-side .attributes,
						.mobile-side .user-benefits {
							width: 100%;
						}
						.mobile-side .attr-title,
						.mobile-side .benefit-title,
						.mobile-side .attr-label,
						.mobile-side .attr-value,
						.mobile-side .benefit-label,
						.mobile-side .benefit-value {
							font-size: 13.394px;
							line-height: 1.5;
						}
						.mobile-side .line-break {
							width: 100%;
						}
						.mobile-side .attr-row,
						.mobile-side .benefit-row {
							gap: 6px;
							align-items: center;
						}
						.mobile-side .attr-label,
						.mobile-side .benefit-label {
							margin-left: 0;
						}
					`}</style>
				</div>
			</motion.div>

			<AnyAlertDialog
				isOpen={isComingSoonOpen}
				leastDestructiveRef={comingSoonCancelRef}
				onClose={onComingSoonClose}
				isCentered
			>
				<AnyAlertDialogOverlay>
					<AnyAlertDialogContent>
						<AnyAlertDialogHeader fontSize="lg" fontWeight="bold">
							Fashion Index relaunch coming soon
						</AnyAlertDialogHeader>
						<AnyAlertDialogBody>
							The Fashion Index is being relaunched. For now, you can view your
							purchased items on your profile.
						</AnyAlertDialogBody>
						<AnyAlertDialogFooter>
							<AnyButton ref={comingSoonCancelRef} onClick={onComingSoonClose}>
								Cancel
							</AnyButton>
							<AnyButton colorScheme="blue" onClick={continueToMyHapes} ml={3}>
								Go to Profile
							</AnyButton>
						</AnyAlertDialogFooter>
					</AnyAlertDialogContent>
				</AnyAlertDialogOverlay>
			</AnyAlertDialog>
		</div>
	)
}

export default WearNowPanelContent
